/**
 * Role-Protected Route Component
 * Checks EntraID group membership before rendering children
 */

import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { checkIsMCAdmin } from '../../services/mcAPI';

const LEADING_API_URL = process.env.REACT_APP_API_URL || 'https://nh9pshlcw7.execute-api.us-west-2.amazonaws.com/prod';

const RoleProtectedRoute = ({ requiredRole, children }) => {
  const { isAuthenticated, isLoading, getAccessToken } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasRole, setHasRole] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      verifyRole();
    }
  }, [isAuthenticated, isLoading]);

  const verifyRole = async () => {
    try {
      const accessToken = await getAccessToken();

      if (requiredRole === 'leading') {
        const res = await fetch(`${LEADING_API_URL}/leading/api/users/me/is-admin`, {
          headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setHasRole(data.data?.isAdmin === true);
        }
      } else if (requiredRole === 'mc') {
        const result = await checkIsMCAdmin(accessToken);
        setHasRole(result?.isMCAdmin === true);
      }
    } catch (err) {
      console.error('Role check failed:', err);
      setHasRole(false);
    } finally {
      setChecking(false);
    }
  };

  if (isLoading || checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTopColor: '#8B0000', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#6b7280' }}>Checking access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!hasRole) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', color: '#1f1f1f', marginBottom: '0.75rem' }}>Access Denied</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem' }}>You don't have permission to access this area.</p>
          <a href="/" style={{ display: 'inline-block', padding: '0.75rem 1.5rem', background: '#8d1f26', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600' }}>
            Back to My Surveys
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default RoleProtectedRoute;
